import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI, propertiesAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Users, Plus, Search, Edit2, Trash2, Shield, UserCog, Wrench, Calculator, User, HardHat, RefreshCw, KeyRound, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { statusLabel, statusClass, initials, avatarColor, errMsg } from '../utils/helpers';
import { usePermissions } from '../context/PermissionsContext';

const ROLES     = ['admin','manager','maintenance','accountant','contractor','tenant'];
const ROLE_ICON = { admin:Shield, manager:UserCog, maintenance:Wrench, accountant:Calculator, contractor:HardHat, tenant:User };
const ROLE_CLR  = { admin:'var(--brand)', manager:'var(--emerald)', maintenance:'var(--amber)', accountant:'var(--violet)', contractor:'var(--rose)', tenant:'var(--sky)' };
const EMPTY     = { name:'', email:'', password:'Admin1234!', phone:'', role:'manager', status:'active', properties:[], unit:'' };

export default function UsersPage() {
  const { t }    = useTranslation();
  const { can }  = usePermissions();
  const canCreate = can('users', 'create');
  const canEdit   = can('users', 'edit');
  const canDelete = can('users', 'delete');
  const [users, setUsers]     = useState([]);
  const [props, setProps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [roleF, setRoleF]     = useState('');
  const [modal, setModal]     = useState(false);
  const [edit, setEdit]       = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);

  const [showCreatePwd, setShowCreatePwd] = useState(false);

  // Reset password modal state
  const [resetModal,  setResetModal]  = useState(false);
  const [resetTarget, setResetTarget] = useState(null);   // { _id, name, email }
  const [resetPwd,    setResetPwd]    = useState('residente#');
  const [showPwd,     setShowPwd]     = useState(false);
  const [copied,      setCopied]      = useState(false);
  const [resetting,   setResetting]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = {}; if (roleF) q.role = roleF; if (search) q.search = search;
    Promise.all([usersAPI.getAll(q), propertiesAPI.getAll()])
      .then(([u,p])=>{ setUsers(u.data.data); setProps(p.data.data); })
      .catch(()=>toast.error(t('failedToLoad')))
      .finally(()=>setLoading(false));
  }, [roleF, search, t]);

  useEffect(()=>{ const timer=setTimeout(load,300); return ()=>clearTimeout(timer); },[load]);

  const openAdd  = () => { setForm(EMPTY); setEdit(null); setShowCreatePwd(false); setModal(true); };
  const openEdit = u  => { setEdit(u._id); setForm({...u, password:'', unit:'', properties:(u.properties||[]).map(p=>p._id||p)}); setShowCreatePwd(false); setModal(true); };

  const tenantCreds = (unit) => ({
    email:    'mercury' + unit.replace(/\s+/g, '').toLowerCase(),
    password: 'residente#' + unit.trim(),
  });

  const handleRoleChange = (role) => {
    setForm(f => {
      const next = { ...f, role };
      if (role === 'tenant' && f.unit) Object.assign(next, tenantCreds(f.unit));
      if (role === 'tenant' && !f.unit) { next.email = ''; next.password = 'residente#'; }
      return next;
    });
  };

  const handleUnitChange = (unit) => {
    setForm(f => ({ ...f, unit, ...tenantCreds(unit) }));
  };

  const handleSave = async () => {
    if (!form.name||!form.email) return toast.warning(t('fullName') + ' ' + t('email') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...form };
      if (edit) { delete data.password; await usersAPI.update(edit,data); toast.success(t('userUpdated')); }
      else       { await usersAPI.create(data); toast.success(t('userCreated')); }
      setModal(false); load();
    } catch(err){ toast.error(errMsg(err)); }
    finally{ setSaving(false); }
  };

  const deleteUser = (id, name) => {
    toast.confirm(
      `${t('deleteUserConfirm') || 'Permanently delete'} ${name}? ${t('cannotBeUndone') || 'This cannot be undone.'}`,
      async () => {
        try { await usersAPI.delete(id); toast.success(t('userDeleted') || 'User deleted'); load(); }
        catch (err) { toast.error(errMsg(err)); }
      },
      t('delete') || 'Delete',
    );
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPwd('residente#');
    setShowPwd(false);
    setCopied(false);
    setResetModal(true);
  };

  const handleReset = async () => {
    if (!resetPwd || resetPwd.length < 6) return toast.error(t('min6Characters') || 'Minimum 6 characters');
    setResetting(true);
    try {
      await usersAPI.resetPassword(resetTarget._id, resetPwd);
      toast.success(t('passwordReset') || 'Password reset successfully');
      setResetModal(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setResetting(false); }
  };

  const copyPwd = () => {
    navigator.clipboard.writeText(resetPwd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toggleProp = id => setForm(f=>({ ...f, properties: f.properties.includes(id)?f.properties.filter(p=>p!==id):[...f.properties,id] }));

  const timeAgo = iso => { if(!iso)return'Never'; const d=Math.floor((Date.now()-new Date(iso))/86400000); return d===0?'Today':d===1?'Yesterday':`${d}d ago`; };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{t('users')}</h1><p className="page-subtitle">{users.filter(u=>u.status==='active').length} {t('activeTeamMembers')}</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {canCreate && <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/>{t('addUser')}</button>}
        </div>
      </div>

      {/* Role summary */}
      <div className="grid grid-4 mb-6">
        {ROLES.map(r=>{
          const Icon  = ROLE_ICON[r];
          const color = ROLE_CLR[r];
          const count = users.filter(u=>u.role===r&&u.status==='active').length;
          return (
            <div key={r} className="card" style={{ padding:'16px 18px', display:'flex', alignItems:'center', gap:14, cursor:'pointer', border:roleF===r?`1.5px solid ${color}`:'1px solid var(--border)', background:roleF===r?color+'08':'' }}
              onClick={()=>setRoleF(roleF===r?'':r)}>
              <div style={{ width:40,height:40,borderRadius:10,background:color+'18',display:'flex',alignItems:'center',justifyContent:'center',color }}><Icon size={20}/></div>
              <div><div style={{ fontSize:22,fontWeight:800 }}>{count}</div><div style={{ fontSize:12,color:'var(--ink-40)' }}>{t(r)}</div></div>
            </div>
          );
        })}
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}><Search size={15} color="var(--ink-20)"/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('searchUsers')}/></div>
        <div className="tabs">
          <div className={`tab${roleF===''?' active':''}`} onClick={()=>setRoleF('')}>{t('all')}</div>
          {ROLES.map(r=><div key={r} className={`tab${roleF===r?' active':''}`} onClick={()=>setRoleF(r)}>{t(r)}</div>)}
        </div>
      </div>

      {loading?<div style={{ display:'flex',justifyContent:'center',padding:60 }}><div className="spinner"/></div>:(
        <div className="grid grid-3">
          {users.map(u=>{
            const Icon  = ROLE_ICON[u.role]||Shield;
            const color = ROLE_CLR[u.role]||'var(--brand)';
            const userProps = (u.properties||[]).map(p=>props.find(x=>x._id===(p._id||p))?.name).filter(Boolean);
            return (
              <div key={u._id} className="card">
                <div style={{ padding:'18px 20px 14px', display:'flex', gap:12, alignItems:'flex-start' }}>
                  <div className="avatar avatar-lg" style={{ background:avatarColor(u.name)+'25',color:avatarColor(u.name),fontWeight:800,fontSize:17,flexShrink:0 }}>
                    {initials(u.name)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:15, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize:12, color:'var(--ink-40)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                    <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                      <span className="badge" style={{ background:color+'15',color }}><Icon size={10}/>{u.role}</span>
                      <span className={`badge ${statusClass(u.status)}`}>{statusLabel(u.status)}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    {canEdit   && <button className="btn btn-ghost btn-icon" title={t('resetPassword') || 'Reset password'} onClick={()=>openReset(u)}><KeyRound size={14}/></button>}
                    {canEdit   && <button className="btn btn-ghost btn-icon" onClick={()=>openEdit(u)}><Edit2 size={14}/></button>}
                    {canDelete && <button className="btn btn-ghost btn-icon" style={{ color:'var(--rose)' }} title={t('delete')} onClick={()=>deleteUser(u._id, u.name)}><Trash2 size={14}/></button>}
                  </div>
                </div>
                <div style={{ padding:'10px 20px 14px', borderTop:'1px solid var(--border-subtle)', background:'var(--surface-2)' }}>
                  {[[t('phone'),u.phone||'—'],[t('lastLogin'),timeAgo(u.lastLogin)]].map(([k,v])=>(
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5 }}>
                      <span style={{ color:'var(--ink-40)' }}>{k}</span><span style={{ fontWeight:500 }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ fontSize:12, color:'var(--ink-40)', marginTop:6 }}>
                    {userProps.length>0
                      ? <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{userProps.map(n=><span key={n} className="badge badge-neutral">{n}</span>)}</div>
                      : t('noPropertiesAssigned')}
                  </div>
                </div>
              </div>
            );
          })}
          {users.length===0&&<div className="empty-state" style={{ gridColumn:'1/-1' }}><Users size={40}/><h3>{t('noUsersFound')}</h3></div>}
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      <Modal open={resetModal} onClose={()=>setResetModal(false)} title={t('resetPassword') || 'Reset Password'} size="sm"
        footer={
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
            <button className="btn btn-secondary" onClick={()=>setResetModal(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={handleReset} disabled={resetting}>
              {resetting ? (t('saving') || 'Saving…') : (t('resetPassword') || 'Reset Password')}
            </button>
          </div>
        }>
        {resetTarget && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* User info */}
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10, background:'var(--surface-2)', border:'1px solid var(--border-subtle)' }}>
              <div className="avatar" style={{ background:avatarColor(resetTarget.name)+'25', color:avatarColor(resetTarget.name), fontWeight:800, fontSize:15, flexShrink:0 }}>
                {initials(resetTarget.name)}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{resetTarget.name}</div>
                <div style={{ fontSize:12, color:'var(--ink-40)' }}>{resetTarget.email}</div>
              </div>
            </div>

            {/* Hint */}
            <div style={{ fontSize:12, color:'var(--ink-50)', padding:'10px 12px', borderRadius:8, background:'rgba(99,102,241,0.07)', border:'1px solid rgba(99,102,241,0.15)' }}>
              <strong>{t('defaultPasswordHint') || 'Default format:'}</strong> &nbsp;
              <code style={{ fontFamily:'monospace', fontWeight:700 }}>residente#</code> + unit &nbsp;
              <span style={{ color:'var(--ink-40)' }}>{t('example') || 'e.g.'} <code>residente#A1</code></span>
            </div>

            {/* New password field */}
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">{t('newPassword') || 'New Password'}</label>
              <div style={{ position:'relative' }}>
                <input
                  className="form-input"
                  type={showPwd ? 'text' : 'password'}
                  value={resetPwd}
                  onChange={e => setResetPwd(e.target.value)}
                  style={{ paddingRight: 76, fontFamily:'monospace', letterSpacing:'0.05em' }}
                  autoFocus
                />
                <div style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', display:'flex', gap:4 }}>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={copyPwd} title={t('copy') || 'Copy'}>
                    {copied ? <Check size={14} color="var(--emerald)"/> : <Copy size={14}/>}
                  </button>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={()=>setShowPwd(v=>!v)}>
                    {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
                  </button>
                </div>
              </div>
              <span style={{ fontSize:11, color:'var(--ink-40)', marginTop:4, display:'block' }}>
                {t('min6Characters') || 'Minimum 6 characters'}
              </span>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={modal} onClose={()=>setModal(false)} title={edit?t('editUser'):t('addUser')}
        footer={<><button className="btn btn-secondary" onClick={()=>setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?t('saving'):t('save')}</button></>}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="form-grid form-grid-2" style={{ gap:16 }}>

            {/* Full name — always full width */}
            <div className="form-group" style={{ gridColumn:'1/-1' }}>
              <label className="form-label">{t('fullName')} *</label>
              <input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Jane Smith"/>
            </div>

            {/* Role + Status — always visible */}
            <div className="form-group">
              <label className="form-label">{t('role')}</label>
              <select className="form-input form-select" value={form.role} onChange={e=>handleRoleChange(e.target.value)}>
                {ROLES.map(r=><option key={r} value={r}>{t(r)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('status')}</label>
              <select className="form-input form-select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                <option value="active">{t('active')}</option>
                <option value="inactive">{t('inactive')}</option>
              </select>
            </div>

            {/* ── Tenant quick-setup: unit drives username + password ── */}
            {!edit && form.role === 'tenant' && (<>
              <div style={{ gridColumn:'1/-1', padding:'10px 13px', borderRadius:9, background:'rgba(14,165,233,0.07)', border:'1px solid rgba(14,165,233,0.2)', fontSize:12, color:'var(--ink-50)' }}>
                <strong style={{ color:'var(--sky)' }}>✦ {t('tenantSetup') || 'Tenant setup'}</strong>
                &nbsp;— {t('tenantSetupHint') || 'Enter the unit and username + password are generated automatically.'}
              </div>
              <div className="form-group">
                <label className="form-label">{t('unit')} *</label>
                <input className="form-input" value={form.unit} onChange={e=>handleUnitChange(e.target.value)}
                  placeholder="A1" autoFocus style={{ fontWeight:700, letterSpacing:'0.04em' }}/>
              </div>
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 301-555-0100"/>
              </div>
            </>)}

            {/* ── Staff: phone ── */}
            {(edit || form.role !== 'tenant') && (
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 301-555-0100"/>
              </div>
            )}

            {/* Username / email — always visible, auto-filled for tenant */}
            <div className="form-group" style={{ gridColumn:'1/-1' }}>
              <label className="form-label">{t('username')} *</label>
              <input className="form-input" type="text" value={form.email}
                onChange={e=>setForm(f=>({...f,email:e.target.value}))}
                placeholder={form.role==='tenant' ? 'mercuryA1' : (t('usernamePlaceholder') || 'jane@company.com')}
                style={form.role==='tenant' && !edit ? { fontFamily:'monospace', letterSpacing:'0.04em' } : {}}
              />
              <span style={{ fontSize:11, color:'var(--ink-40)', marginTop:4, display:'block' }}>
                {form.role==='tenant'
                  ? (t('usernameHint') || 'Auto-filled as mercury + unit. You can edit it.')
                  : (t('staffUsernameHint') || 'Use an email address for staff members.')}
              </span>
            </div>

            {/* Password — only on create, with show/hide toggle */}
            {!edit && (
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">{t('password')}</label>
                <div style={{ position:'relative' }}>
                  <input className="form-input" type={showCreatePwd ? 'text' : 'password'}
                    value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                    placeholder={t('min6Characters')}
                    style={form.role==='tenant' ? { fontFamily:'monospace', letterSpacing:'0.05em', paddingRight:44 } : { paddingRight:44 }}
                  />
                  <button type="button" className="btn btn-ghost btn-icon btn-sm"
                    onClick={()=>setShowCreatePwd(v=>!v)}
                    style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)' }}>
                    {showCreatePwd ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
                {form.role==='tenant' && (
                  <span style={{ fontSize:11, color:'var(--ink-40)', marginTop:4, display:'block' }}>
                    {t('tenantPasswordHint') || 'Auto-filled as residente# + unit. You can edit it.'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Property access */}
          <div className="form-group">
            <label className="form-label">{t('propertyAccess')}</label>
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:4 }}>
              {props.map(p=>(
                <label key={p._id} style={{ display:'flex',alignItems:'center',gap:9,cursor:'pointer',padding:'8px 12px',borderRadius:8,border:'1px solid var(--border)',background:(form.properties||[]).includes(p._id)?'var(--brand-ghost)':'transparent',transition:'background 120ms' }}>
                  <input type="checkbox" checked={(form.properties||[]).includes(p._id)} onChange={()=>toggleProp(p._id)}/>
                  <span style={{ fontSize:14,fontWeight:500 }}>{p.name}</span>
                  <span style={{ fontSize:12,color:'var(--ink-40)',marginLeft:'auto' }}>{p.city}, {p.state}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
